import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HiEye } from 'react-icons/hi';
import { FaPencilAlt } from 'react-icons/fa';
import { TbRulerMeasure } from 'react-icons/tb';
import { BsGrid3X3 } from 'react-icons/bs';
import { HiOutlineCog } from 'react-icons/hi';
import { MdOutlineTableChart } from 'react-icons/md';

import { Text, toasts, ModalMethods } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import {
  getValueFromLocalStorage,
  convertNumberOrStringToPriceFormat,
  setValueInLocalStorage,
} from '../../../../utils/common';

import piece from '../../../../assets/icons/Stack.png';
import wear from '../../../../assets/icons/Tag.png';
import PiecesModal from './pieces/PiecesModal';
import AddPieceModal, { OutfitPieceType } from './pieces/AddPieceModal';
import EditPieceModal from './pieces/EditPieceModal';
import PricingModal from './PricingModal';
import MeasurementsModal from './measurement/MeasurementsModal';
import SizeChartModal from './measurement/SizeChartModal';
import ProductionProcessModal from './ProductionProcessModal';
import OptionsModal from './options/OptionsModal';

type OutfitPiece = {
  id: number;
  name: string;
  outfit_link?: string;
  icon_reference_id?: string;
};

type TaxData = {
  name: string;
  value: number;
  is_default?: boolean | null;
};

type PricingData = {
  name: string;
  value: number;
  is_default?: boolean | null;
};

type PricingRule = {
  id: number;
  operator: string;
  price: string;
  gst: number;
};

type PricingMetaData = {
  id?: number;
  custom_outfit_id?: number;
  default_price?: PricingData[];
  pricing_rules?: PricingRule[];
  advance_tax_name?: string;
  flat_gst?: number;
  flat_tax_name?: string;
  no_tax?: boolean;
  is_flat_gst_default?: boolean;
  tax_type?: 'NOTAX' | 'FLAT' | 'ADVANCE';
};

type ItemData = {
  id: number;
  boutique_id: number;
  outfit_name: string;
  reference_id: string;
  icon_url: string;
  outfit_pieces: OutfitPiece[];
  pieces: number;
  created_at: string;
  is_valid: boolean;
  category: string | null;
  base_price: number | null;
  stitch_options_price: number;
  Processes: number;
  tax_data: TaxData;
  pricing_data?: PricingData[];
  pricing_meta_data?: PricingMetaData;
  min_value?: number;
  max_value?: number;
  stitch_options_count?: number;
  measurement_count?: number;
};

type MeasurementParam = {
  name: string;
  icon_url: string;
};

type MeasurementParams = {
  id: number;
  params: MeasurementParam[];
  measurement_scale: {
    scale: string;
  };
};

type StitchOptionValue = {
  field_label: string;
  price: number;
};

type StitchOption = {
  id: number;
  key: string;
  values: StitchOptionValue[];
  type: 'RADIO' | 'MULTI_SELECT' | 'COUNTER' | 'MULTI_LINE_INPUT';
  min_value: number | null;
  max_value: number | null;
  placeholder: string | null;
};

type ProductionStep = {
  process_name: string;
  days_to_be_take: number;
  worker_cost_to_be_take: number;
  material_cost_to_be_take: number;
  workers: Array<{
    id: number;
    user_id: number;
    user_name: string;
    user_phone_number: string;
    skilled_role_names: string[] | null;
    administrative_role_name: string | null;
  }>;
  sort_index: number;
};

type ProductionProcess = {
  boutique_id: number;
  atomic_outfit_id: number;
  steps: ProductionStep[];
};

type SizeCharts = {
  [key: string]: {
    XS: number;
    S: number;
    M: number;
    L: number;
    XL: number;
    XXL: number;
  };
};

type PieceDetails = {
  measurementParams: MeasurementParams | null;
  stitchOptions: {
    response: StitchOption[];
    message: string | null;
  } | null;
  productionProcess: ProductionProcess | null;
  sizeCharts: SizeCharts | null;
};

const EditItem = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [itemData, setItemData] = useState<ItemData | null>(null);
  const [isItemLevel, setIsItemLevel] = useState<boolean>(() => {
    const storedValue = getValueFromLocalStorage('edit_item_view_level');

    if (storedValue === 'piece') {
      return false;
    }

    return true;
  });
  const piecesModalRef = useRef<ModalMethods>(null);
  const addPieceModalRef = useRef<ModalMethods>(null);
  const editPieceModalRef = useRef<ModalMethods>(null);
  const pricingModalRef = useRef<ModalMethods>(null);
  const measurementsModalRef = useRef<ModalMethods>(null);
  const sizeChartModalRef = useRef<ModalMethods>(null);
  const productionProcessModalRef = useRef<ModalMethods>(null);
  const optionsModalRef = useRef<ModalMethods>(null);
  const [selectedPiece, setSelectedPiece] = useState<OutfitPiece | null>(null);
  const [selectedPieceId, setSelectedPieceId] = useState<number | null>(null);
  const [pieceDetails, setPieceDetails] = useState<PieceDetails | null>(null);
  const [isLoadingPieceDetails, setIsLoadingPieceDetails] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      fetchItemData();
    }
  }, [id]);

  useEffect(() => {
    if (itemData?.outfit_pieces && itemData.outfit_pieces.length > 0 && selectedPieceId === null) {
      const firstPiece = itemData.outfit_pieces[0];
      setSelectedPieceId(firstPiece.id);
    }
  }, [itemData, selectedPieceId]);

  useEffect(() => {
    setValueInLocalStorage('edit_item_view_level', isItemLevel ? 'item' : 'piece');
  }, [isItemLevel]);

  const fetchItemData = async () => {
    try {
      setIsLoading(true);
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-error');
        navigate('/settings/items-and-categories');

        return;
      }

      const response = await api.getRequest(`custom_outfit/${id}`);

      if (response.status) {
        setItemData(response.data?.custom_outfit_response || null);
      } else {
        toasts('error', response.message || 'Failed to load item data', 'item-load-error');
        navigate('/settings/items-and-categories');
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'item-load-error');
        navigate('/settings/items-and-categories');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchItemDatap = async () => {
    try {
      const boutique_id = getValueFromLocalStorage('boutique_id');
      piecesModalRef.current?.show();

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-error');
        navigate('/settings/items-and-categories');

        return;
      }

      const response = await api.getRequest(`custom_outfit/${id}`);

      if (response.status) {
        setItemData(response.data?.custom_outfit_response || null);
      } else {
        toasts('error', response.message || 'Failed to load item data', 'item-load-error');
        navigate('/settings/items-and-categories');
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'item-load-error');
        navigate('/settings/items-and-categories');
      }
    }
  };

  // const handleSave = async () => {
  //   // TODO: Implement save functionality
  //   toasts('success', 'Item saved successfully', 'item-saved-success');
  // };

  const handleDeletePiece = async (pieceId: number) => {
    try {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-error');

        return;
      }

      if (!id) {
        toasts('error', 'Custom outfit ID not found', 'custom-outfit-id-error');

        return;
      }

      const response = await api.putRequest(
        `outfit_piece/delete/${pieceId}?custom_outfit_id=${id}`,
        {}
      );

      if (response.status) {
        // Update local state to remove the deleted piece
        if (itemData) {
          const updatedPieces =
            itemData.outfit_pieces?.filter((piece) => piece.id !== pieceId) || [];
          setItemData({
            ...itemData,
            outfit_pieces: updatedPieces,
            pieces: updatedPieces.length,
          });
        }
        toasts('success', 'Piece deleted successfully', 'piece-delete-success');
      } else {
        toasts('error', response.message || 'Failed to delete piece', 'piece-delete-error');
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'piece-delete-error');
      }
    }
  };

  const fetchPieceDetails = useCallback(async (atomicOutfitId: number) => {
    try {
      setIsLoadingPieceDetails(true);
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-error');

        return;
      }

      const response = await api.getRequest(
        `outfit_piece/fetch/all_details?boutique_id=${boutique_id}&atomic_outfit_id=${atomicOutfitId}`
      );

      if (response.status && response.data) {
        setPieceDetails({
          measurementParams: response.data.measurementParams || null,
          stitchOptions: response.data.stitchOptions || null,
          productionProcess: response.data.productionProcess || null,
          sizeCharts: response.data.sizeCharts || null,
        });
      } else {
        setPieceDetails({
          measurementParams: null,
          stitchOptions: null,
          productionProcess: null,
          sizeCharts: null,
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'piece-details-error');
      }
      setPieceDetails({
        measurementParams: null,
        stitchOptions: null,
        productionProcess: null,
        sizeCharts: null,
      });
    } finally {
      setIsLoadingPieceDetails(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPieceId) {
      fetchPieceDetails(selectedPieceId);
    }
  }, [selectedPieceId, fetchPieceDetails]);

  const handleAddPiece = async (piece: OutfitPieceType) => {
    try {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-error');

        return;
      }

      if (!id) {
        toasts('error', 'Item ID not found', 'item-id-error');

        return;
      }

      // Add piece to the outfit
      const response = await api.putRequest(`custom_outfit/update/${id}`, {
        atomic_outfit_details: [{ id: piece.id }],
      });

      if (response.status) {
        // Refresh item data and keep PiecesModal open (add is from within PiecesModal)
        await fetchItemDatap();
        toasts('success', 'Piece added successfully', 'piece-add-success');
      } else {
        toasts('error', response.message || 'Failed to add piece', 'piece-add-error');
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'piece-add-error');
      }
    }
  };

  const renderItemLevelView = () => {
    if (!itemData) {
      return null;
    }

    const minPrice = itemData.min_value || 0;
    const maxPrice = itemData.max_value || 0;

    const getTaxDisplayText = (): string => {
      const meta = itemData.pricing_meta_data;
      const taxType = meta?.tax_type;

      if (!meta?.tax_type) {
        return itemData.tax_data?.name && itemData.tax_data?.value !== undefined
          ? `${itemData.tax_data.name} ${itemData.tax_data.value}%`
          : 'No Tax';
      }

      if (taxType === 'ADVANCE') {
        const taxName = meta?.advance_tax_name || 'GST';
        const gstValues =
          meta?.pricing_rules
            ?.map((r) => r.gst)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .sort((a, b) => a - b) ?? [];
        const valueStr = gstValues.length > 0 ? `(${gstValues.map((v) => `${v}%`).join('-')})` : '';

        return `${taxName} ${valueStr}`.trim();
      }

      if (taxType === 'FLAT') {
        const taxName = meta?.flat_tax_name || 'GST';
        const gst = meta?.flat_gst ?? 0;

        return `${taxName} (${gst}%)`;
      }

      return 'No Tax';
    };

    const taxDisplayText = getTaxDisplayText();

    return (
      <div className="flex gap-6 px-6">
        <div className="flex-1 max-w-[600px] space-y-6">
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <p className="font-inter font-semibold text-base">
                  Pieces in this item - {itemData.pieces || itemData.outfit_pieces?.length || 0}
                </p>
              </div>
              <button
                type="button"
                onClick={() => piecesModalRef.current?.show()}
                className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
              >
                <FaPencilAlt size={14} />
                <span>Modify</span>
              </button>
            </div>
            <div className="max-h-[240px] overflow-y-auto space-y-2 pr-2">
              {itemData.outfit_pieces?.map((piece, index) => (
                <div
                  key={piece.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
                >
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    {piece.outfit_link ? (
                      <img
                        src={piece.outfit_link}
                        alt={piece.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">+</span>
                    )}
                  </div>
                  <p className="font-inter text-sm">
                    {index + 1}. {piece.name}
                  </p>
                </div>
              ))}
            </div>
            {itemData.outfit_pieces && itemData.outfit_pieces.length > 3 && (
              <button
                type="button"
                className="w-full py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Scroll for more
              </button>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="font-inter font-semibold text-[15px] leading-tight flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#EEF1FF]">
                    <span className="text-[14px]">🏷️</span>
                  </span>
                  <span>Pricing and tax</span>
                </p>
                <p className="font-inter text-sm text-[#4B5563] mt-1 max-w-xl">
                  Set per-piece pricing, minimum/maximum rates, and add stitching, fabric, and
                  custom charges. Configure GST or tax values for this piece.
                </p>
              </div>
              <button
                type="button"
                onClick={() => pricingModalRef.current?.show()}
                className="px-4 py-2 border border-[#2563EB] text-[#2563EB] rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#EFF6FF] transition-colors"
              >
                <FaPencilAlt size={14} />
                <span>Modify</span>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-baseline gap-2">
                  <p className="font-inter font-semibold text-sm text-black">Pricing</p>
                  {itemData.min_value !== undefined &&
                  itemData.max_value !== undefined &&
                  (itemData.min_value > 0 || itemData.max_value > 0) ? (
                    <p className="font-inter text-sm text-[#4B5563]">
                      Min ₹{convertNumberOrStringToPriceFormat(itemData.min_value ?? 0)} • Max ₹
                      {convertNumberOrStringToPriceFormat(itemData.max_value ?? 0)}
                    </p>
                  ) : (
                    <p className="font-inter text-sm text-[#4B5563]">Price range not set</p>
                  )}
                </div>

                <div className="flex gap-3 mt-3 flex-wrap">
                  {itemData.pricing_data && itemData.pricing_data.length > 0 ? (
                    itemData.pricing_data.map((pricing, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center px-4 py-2 rounded-lg bg-[#E5E5EA] text-xs font-semibold ${
                          pricing.is_default ? 'text-[#16A34A]' : 'text-[#111827]'
                        }`}
                      >
                        <span className="mr-1">{pricing.name}</span>
                        <span>₹{convertNumberOrStringToPriceFormat(pricing.value || 0)}</span>
                      </span>
                    ))
                  ) : (
                    <p className="font-inter text-sm text-[#4B5563]">No pricing data</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="font-inter font-semibold text-sm text-black">Tax Percentage</p>
                <div className="flex items-center gap-6">
                  <p className="font-inter text-sm text-[#4B5563]">{taxDisplayText}</p>
                  <button
                    type="button"
                    className="text-[#16A34A] text-sm font-semibold hover:underline"
                  >
                    Default Price
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 max-w-[500px] flex justify-center items-start">
          <div className="w-full bg-black rounded-2xl p-5 shadow-lg sticky top-6">
            <div className="flex gap-4 mb-4">
              <div className="w-24 h-24 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {itemData.icon_url ? (
                  <img
                    src={itemData.icon_url}
                    alt={itemData.outfit_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl">👕</span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start mb-3 gap-2">
                  <Text
                    className="font-inter font-bold text-base text-white text-[1.1rem] line-clamp-2 leading-tight"
                    color="white"
                  >
                    {itemData.outfit_name}
                  </Text>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-white hover:text-gray-200"
                  >
                    <HiEye size={18} />
                    <Text size="medium" color="white">
                      Preview
                    </Text>
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs text-white mb-3">
                  <div className="flex items-center gap-1.5">
                    <img src={piece} alt="Piece" className="w-3.5 h-3.5 object-contain" />
                    <span>{itemData.pieces === 1 ? 'Single Piece' : 'Multi Piece'}</span>
                  </div>
                  {itemData.category && (
                    <div className="flex items-center gap-1.5">
                      <img src={wear} alt="Category" className="w-3.5 h-3.5 object-contain" />
                      <span>{itemData.category}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <BsGrid3X3 size={14} />
                    <span>{itemData.stitch_options_count ?? 0}</span>
                    <span>Options</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <TbRulerMeasure size={14} />
                    <span>{itemData.measurement_count ?? 0}</span>
                    <span>Measurements</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <HiOutlineCog size={14} />
                    <span>{itemData.Processes ?? 0}</span>
                    <span> Processes</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
              <Text className="text-sm font-semibold text-white" color="white">
                Item Total: ₹{convertNumberOrStringToPriceFormat(minPrice)} - ₹
                {convertNumberOrStringToPriceFormat(maxPrice)}
              </Text>
              <Text className="text-sm font-medium text-white" color="white">
                {taxDisplayText}
              </Text>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPieceLevelView = () => {
    if (!itemData) {
      return null;
    }

    const measurementParams = pieceDetails?.measurementParams;
    const stitchOptions = pieceDetails?.stitchOptions;
    const productionProcess = pieceDetails?.productionProcess;
    const sizeCharts = pieceDetails?.sizeCharts;

    const hasMeasurements = measurementParams?.params && measurementParams.params.length > 0;
    const hasOptions = stitchOptions?.response && stitchOptions.response.length > 0;
    const hasProductionProcess = productionProcess?.steps && productionProcess.steps.length > 0;
    const hasSizeCharts = sizeCharts && Object.keys(sizeCharts).length > 0;

    const getOptionTypeLabel = (type: string) => {
      switch (type) {
        case 'RADIO':
          return 'Single Select';
        case 'MULTI_SELECT':
          return 'Multi Select';
        case 'COUNTER':
          return 'Quantity';
        case 'MULTI_LINE_INPUT':
          return 'Text Input';
        default:
          return type;
      }
    };

    const formatMeasurementName = (name: string) => {
      return name
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const sizeChartColumns = hasSizeCharts && sizeCharts ? Object.keys(sizeCharts) : [];
    const sizeChartSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

    return (
      <div className=" flex flex-col gap-6 px-6">
        <div className=" w-full flex flex-col">
          <div className="  flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={piece} alt="Pieces" className="w-5 h-5" />
              <Text className="font-inter font-semibold text-base" color="black">
                Pieces in this item - {itemData.pieces || itemData.outfit_pieces?.length || 0}
              </Text>
            </div>
            <button
              type="button"
              onClick={() => piecesModalRef.current?.show()}
              className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
            >
              <FaPencilAlt size={14} />
              <span>Modify</span>
            </button>
          </div>
          <div className=" w-[900px] flex gap-2 overflow-x-auto pb-2">
            {itemData.outfit_pieces?.map((piece, index) => {
              const isSelected = selectedPieceId === piece.id;

              return (
                <button
                  key={piece.id}
                  type="button"
                  onClick={() => setSelectedPieceId(piece.id)}
                  className={`flex items-center gap-2 p-3 rounded-t-xl  min-w-[200px] flex-shrink-0 transition-colors ${
                    isSelected
                      ? 'border-blue-5 border-b-2 border-b-black'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    {piece.outfit_link ? (
                      <img
                        src={piece.outfit_link}
                        alt={piece.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <img
                        src={itemData.icon_url}
                        alt={piece.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    )}
                  </div>
                  <Text
                    className={`font-inter text-sm ${isSelected ? 'font-[600]' : 'font-[400]'}`}
                    color="black"
                  >
                    {index + 1}. {piece.name}
                  </Text>
                </button>
              );
            })}
          </div>
        </div>

        <div className=" flex flex-col gap-4">
          {/* Measurements Card */}
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <TbRulerMeasure size={20} className="text-gray-600" />
                <div>
                  <p className="font-inter font-semibold text-[1rem]" color="black">
                    Measurements{' '}
                    {measurementParams?.measurement_scale?.scale && (
                      <span className="font-normal text-gray-500">
                        ({measurementParams.measurement_scale.scale})
                      </span>
                    )}
                  </p>
                  <Text className="font-inter text-xs text-gray-600 mt-1" size="small">
                    For each piece, pick measurements from Darzee&apos;s library (with reference
                    images) or add custom fields with your own images.
                  </Text>
                </div>
              </div>
              <button
                type="button"
                onClick={() => measurementsModalRef.current?.show()}
                className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
              >
                <FaPencilAlt size={14} />
                <span>Modify</span>
              </button>
            </div>
            {isLoadingPieceDetails ? (
              <div className="text-center py-4">
                <Text className="font-inter text-sm text-gray-500" color="gray">
                  Loading...
                </Text>
              </div>
            ) : hasMeasurements && measurementParams ? (
              <div className="grid grid-cols-3 gap-2">
                {measurementParams.params.map((param, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 font-inter font-[500]  ">
                    {/* <img src={param.icon_url} alt={param.name} className="w-6 h-6 object-contain" /> */}
                    <Text className="font-inter text-sm" color="black">
                      {idx + 1}. {formatMeasurementName(param.name)}
                    </Text>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <Text className="font-inter text-sm text-gray-500" color="gray">
                  No measurements configured
                </Text>
              </div>
            )}
          </div>

          {/* Options Card */}
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <BsGrid3X3 size={20} className="text-gray-600" />
                <div>
                  <p className="font-inter font-semibold text-[1rem]">Options</p>
                  <Text className="font-inter text-xs text-gray-600 mt-1" size="small">
                    For each piece, select style options from Darzee&apos;s library (like fits,
                    sleeves, collars) or create your own custom options with images.
                  </Text>
                </div>
              </div>
              <button
                type="button"
                onClick={() => optionsModalRef.current?.show()}
                className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
              >
                <FaPencilAlt size={14} />
                <span>Modify</span>
              </button>
            </div>
            {isLoadingPieceDetails ? (
              <div className="text-center py-4">
                <Text className="font-inter text-sm text-gray-500" color="gray">
                  Loading...
                </Text>
              </div>
            ) : hasOptions && stitchOptions ? (
              <div className="space-y-4">
                {stitchOptions.response.map((option) => (
                  <div key={option.id} className="space-y-2">
                    <div className="flex items-center gap-5">
                      <p className="font-inter  text-sm  font-[600] " color="black">
                        {option.key}
                      </p>
                      <Text className="font-inter text-xs text-gray-600" color="gray">
                        {getOptionTypeLabel(option.type)}
                        {option.type === 'RADIO' || option.type === 'MULTI_SELECT'
                          ? ` • ${option.values.length} Options`
                          : ''}
                        {option.type === 'COUNTER'
                          ? ` • Min ${option.min_value || 0} • Max ${
                              option.max_value || 0
                            } • Default ${option.min_value || 0}`
                          : ''}
                      </Text>
                    </div>
                    {option.type === 'RADIO' || option.type === 'MULTI_SELECT' ? (
                      <div className="flex flex-wrap gap-2">
                        {option.values.slice(0, 5).map((value, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="px-3 py-2 border border-[#525252] rounded-lg text-sm hover:bg-gray-50 font-inter font-[500] "
                          >
                            {value.field_label} ₹{convertNumberOrStringToPriceFormat(value.price)}
                          </button>
                        ))}
                        {option.values.length > 5 && (
                          <span className="px-3 py-2 text-sm text-gray-500">
                            +{option.values.length - 5} more
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <Text className="font-inter text-sm text-gray-500" color="gray">
                  No options configured
                </Text>
              </div>
            )}
          </div>

          {/* Production Process Card */}
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <HiOutlineCog size={20} className="text-gray-600" />
                <div>
                  <p className="font-inter font-semibold text-[1rem]" color="black">
                    Production Process
                  </p>
                  <Text className="font-inter text-xs text-gray-600 mt-1" size="small">
                    Define step-by-step production processes for this piece. Add time, per-piece
                    worker cost, material cost and required roles for each step to estimate total
                    effort.
                  </Text>
                </div>
              </div>
              <button
                type="button"
                onClick={() => productionProcessModalRef.current?.show()}
                className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
              >
                <FaPencilAlt size={14} />
                <span>Modify</span>
              </button>
            </div>
            {isLoadingPieceDetails ? (
              <div className="text-center py-4">
                <Text className="font-inter text-sm text-gray-500" color="gray">
                  Loading...
                </Text>
              </div>
            ) : hasProductionProcess && productionProcess ? (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 font-semibold text-gray-700">process name</th>
                        <th className="text-left py-2 font-semibold text-gray-700">Process Days</th>
                        <th className="text-left py-2 font-semibold text-gray-700">Worker Cost</th>
                        <th className="text-left py-2 font-semibold text-gray-700">
                          Material Cost
                        </th>
                        <th className="text-left py-2 font-semibold text-gray-700">Workers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productionProcess.steps.map((step, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="py-2">{step.process_name}</td>
                          <td className="py-2">{step.days_to_be_take}</td>
                          <td className="py-2">
                            ₹{convertNumberOrStringToPriceFormat(step.worker_cost_to_be_take)}
                          </td>
                          <td className="py-2">
                            ₹{convertNumberOrStringToPriceFormat(step.material_cost_to_be_take)}
                          </td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              {step.workers.slice(0, 3).map((worker, wIdx) => (
                                <span key={wIdx} className="text-xs">
                                  {worker.user_name}
                                  {wIdx < step.workers.length - 1 && wIdx < 2 && ','}
                                </span>
                              ))}
                              {step.workers.length > 3 && (
                                <button
                                  type="button"
                                  className="text-blue-600 text-xs hover:underline"
                                >
                                  {step.workers.length - 3} more
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Text className="font-inter text-sm text-gray-500" color="gray">
                  No production process configured
                </Text>
              </div>
            )}
          </div>

          {/* Size Chart Card */}
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <MdOutlineTableChart size={20} className="text-gray-600" />
                <div>
                  <p className="font-inter font-semibold text-[1rem]" color="black">
                    Size Chart
                  </p>
                  <Text className="font-inter text-xs text-gray-600 mt-1" size="small">
                    Create or modify size chart rules for this piece. Add your own size breakpoints,
                    auto-calculate ranges, or import Darzee&apos;s standard templates.
                  </Text>
                </div>
              </div>
              <button
                type="button"
                onClick={() => sizeChartModalRef.current?.show()}
                className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
              >
                <FaPencilAlt size={14} />
                <span>Modify</span>
              </button>
            </div>
            {isLoadingPieceDetails ? (
              <div className="text-center py-4">
                <Text className="font-inter text-sm text-gray-500" color="gray">
                  Loading...
                </Text>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 border border-gray-200 text-center">
                        Size
                      </th>
                      {sizeChartColumns.map((col) => (
                        <th
                          key={col}
                          className=" py-2 px-2 font-semibold items-center text-center text-gray-700 border border-gray-200"
                        >
                          {formatMeasurementName(col)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sizeChartSizes.map((size) => (
                      <tr key={size}>
                        <td className="py-2 px-2 font-semibold border border-gray-200 text-center">
                          {size}
                        </td>
                        {sizeChartColumns.map((col) => (
                          <td key={col} className="py-2 px-2 border border-gray-200 text-center">
                            {sizeCharts?.[col]?.[size as keyof (typeof sizeCharts)[string]] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="w-full h-full bg-[#F8F8FD] flex items-center justify-center">
        <Text color="black">Loading item data...</Text>
      </div>
    );
  }

  if (!itemData) {
    return (
      <div className="w-full h-full bg-[#F8F8FD] flex items-center justify-center">
        <Text color="black">Item not found</Text>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-[#F8F8FD] pt-2 pb-4 overflow-y-auto">
      <div className="w-full flex justify-between items-center mb-2 bg-white py-4 px-6">
        <div>
          <p className="font-inter font-semibold text-[1rem] mb-2 block leading-[100%] tracking-[0.07px]">
            <span className=" text-[1.2rem] font-bold">{itemData.outfit_name}</span> - Define
            pieces, measurements, options and outfit-level pricing for this template.
          </p>
        </div>
        {/* <Button label="Save Item" size="medium" bgColor="primary" onClick={handleSave} /> */}
      </div>

      <div className=" p-2 mb-4 mx-6 w-[50%]">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-inter font-bold text-[1.1rem] mb-1 block" color="black">
              Item Setup
            </p>
            <p className="font-inter text-sm text-[#000000B2] w-[80%]">
              Switch between piece-level & item-level controls
            </p>
          </div>
          <div className="flex items-center bg-[#D9D9D980] rounded-2xl p-1 gap-0">
            <button
              type="button"
              onClick={() => setIsItemLevel(false)}
              className={`px-6 py-2 rounded-l-2xl font-semibold text-sm transition-all duration-0 ease-in-out ${
                !isItemLevel ? 'bg-black text-white rounded-2xl' : 'bg-[#D9D9D980] text-black'
              }`}
            >
              Piece Level
            </button>
            <button
              type="button"
              onClick={() => setIsItemLevel(true)}
              className={`px-6 py-2 rounded-r-2xl font-semibold text-sm transition-all duration-0 ease-in-out ${
                isItemLevel ? 'bg-black text-white rounded-2xl' : 'bg-[#D9D9D980] text-black '
              }`}
            >
              Item Level
            </button>
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden min-h-[200px]">
        <div
          key={isItemLevel ? 'item-level' : 'piece-level'}
          style={{
            animation: 'fadeInSlide 0.5s ease-in-out',
          }}
        >
          {isItemLevel ? renderItemLevelView() : renderPieceLevelView()}
        </div>
      </div>

      {itemData && (
        <>
          <PiecesModal
            ref={piecesModalRef}
            outfitName={itemData.outfit_name}
            outfitPieces={itemData.outfit_pieces || []}
            customOutfitId={Number(id)}
            onEditPiece={(piece) => {
              setSelectedPiece(piece);
              editPieceModalRef.current?.show();
            }}
            onDeletePiece={(pieceId) => {
              handleDeletePiece(pieceId);
            }}
            onAddPiece={() => {
              addPieceModalRef.current?.show();
            }}
            onPieceImageUpdate={async () => {
              await fetchItemDatap();
            }}
          />
          <AddPieceModal ref={addPieceModalRef} onPieceSelect={handleAddPiece} />
          <EditPieceModal
            ref={editPieceModalRef}
            piece={selectedPiece}
            outfitName={itemData.outfit_name}
            customOutfitId={Number(id)}
            onSave={async () => {
              await fetchItemDatap();
            }}
          />
          <PricingModal
            ref={pricingModalRef}
            customOutfitId={Number(id)}
            onSaved={async () => {
              await fetchItemData();
            }}
          />
          <MeasurementsModal
            ref={measurementsModalRef}
            outfitName={itemData.outfit_name}
            outfitPieces={itemData.outfit_pieces || []}
            selectedPieceId={selectedPieceId}
            onPieceChange={(pieceId) => setSelectedPieceId(pieceId)}
            onMeasurementsUpdated={async () => {
              if (selectedPieceId) {
                await fetchPieceDetails(selectedPieceId);
              }
            }}
          />
          <SizeChartModal
            ref={sizeChartModalRef}
            outfitName={itemData.outfit_name}
            outfitPieces={itemData.outfit_pieces || []}
            selectedPieceId={selectedPieceId}
            onPieceChange={(pieceId) => setSelectedPieceId(pieceId)}
            onSaved={async () => {
              if (selectedPieceId) {
                await fetchPieceDetails(selectedPieceId);
              }
            }}
          />
          <ProductionProcessModal
            ref={productionProcessModalRef}
            outfitName={itemData.outfit_name}
            outfitPieces={itemData.outfit_pieces || []}
            selectedPieceId={selectedPieceId}
            onPieceChange={(pieceId) => setSelectedPieceId(pieceId)}
            onSaved={async () => {
              if (selectedPieceId) {
                await fetchPieceDetails(selectedPieceId);
              }
            }}
          />
          <OptionsModal
            ref={optionsModalRef}
            outfitName={itemData.outfit_name}
            outfitPieces={itemData.outfit_pieces || []}
            selectedPieceId={selectedPieceId}
            onPieceChange={(pieceId) => setSelectedPieceId(pieceId)}
            onOptionsUpdated={async () => {
              if (selectedPieceId) {
                await fetchPieceDetails(selectedPieceId);
              }
            }}
          />
        </>
      )}
    </div>
  );
};

export default EditItem;
